import { useState, useCallback } from 'react';
import { useAppSelector } from '../redux/hooks';
import * as fileUploadService from '../services/api/fileUploadService';

// ファイルアップロード関連の型定義
export interface UploadedFile {
  id: string;
  file_id?: string;  // OpenAI file IDを追加
  name: string;
  size: number;
  mime_type?: string;  // MIMEタイプを追加
  service: string;
  status: 'uploading' | 'completed' | 'error';
  progress?: number;
  error?: string;
  s3_url?: string;  // S3のURLを追加
  s3_key?: string;  // S3のキーを追加
  s3_bucket?: string;  // S3のバケット名を追加
}

export interface FileUploadResponse {
  success: boolean;
  file_id?: string;
  s3_url?: string;  // S3のURLを追加
  s3_key?: string;  // S3のキーを追加
  s3_bucket?: string;  // S3のバケット名を追加
  service?: string;
  metadata?: {
    file_name: string;
    file_size: number;
    mime_type: string;
  };
  error?: string;
}

export interface UseFileUploadResult {
  uploadFile: (file: File, aiModel: string, chatId: number) => Promise<FileUploadResponse>;
  uploadProgress: number;
  isUploading: boolean;
  error: string | null;
  uploadedFiles: UploadedFile[];
  cancelUpload: () => void;
  removeFile: (fileId: string) => void;
  clearError: () => void;
  reset: () => void;
}

export const useFileUpload = (): UseFileUploadResult => {
  // 状態管理
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [currentUploadController, setCurrentUploadController] = useState<AbortController | null>(null);

  // Redux stateから認証情報を取得
  // const auth = useAppSelector((state) => state.auth);

  /**
   * ファイルアップロード実行
   */
  const uploadFile = useCallback(async (
    file: File, 
    aiModel: string, 
    chatId: number
  ): Promise<FileUploadResponse> => {
    try {
      setIsUploading(true);
      setError(null);
      setUploadProgress(0);

      // AbortControllerを設定
      const controller = new AbortController();
      setCurrentUploadController(controller);

      // プログレスコールバック
      const onProgress = (progress: number) => {
        setUploadProgress(progress);
      };

      // ファイルアップロードサービスを呼び出し
      const response = await fileUploadService.uploadFile(file, aiModel, chatId, onProgress);

      console.log('📤 useFileUpload: API Response received:', {
        success: response.success,
        file_id: response.file_id,
        s3_url: response.s3_url,
        s3_key: response.s3_key,
        s3_bucket: response.s3_bucket,
        full_response: response
      });

      if (response.success && response.file_id) {
        // アップロード成功時に管理リストに追加
        const uploadedFile: UploadedFile = {
          id: response.file_id,
          file_id: response.file_id,  // file_idも追加
          name: response.metadata?.file_name || file.name,
          size: response.metadata?.file_size || file.size,
          mime_type: file.type,  // MIMEタイプを追加
          service: response.service || aiModel,
          status: 'completed',
          s3_url: response.s3_url,  // S3のURLを追加
          s3_key: response.s3_key,  // S3のキーを追加
          s3_bucket: response.s3_bucket  // S3のバケット名を追加
        };

        console.log('📦 useFileUpload: UploadedFile object created:', uploadedFile);

        setUploadedFiles(prev => [...prev, uploadedFile]);
        setUploadProgress(100);
      } else if (response.error) {
        // APIエラーレスポンスの場合
        setError(response.error);
      }

      return response;

    } catch (err: any) {
      // ネットワークエラーやその他の例外
      const errorMessage = err.message || 'ファイルアップロードに失敗しました';
      setError(errorMessage);
      
      // エラーレスポンスを返す
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setIsUploading(false);
      setCurrentUploadController(null);
    }
  }, []);

  /**
   * アップロードキャンセル
   */
  const cancelUpload = useCallback(() => {
    if (currentUploadController) {
      currentUploadController.abort();
      setCurrentUploadController(null);
    }
    
    setIsUploading(false);
    setUploadProgress(0);
    setError('Upload cancelled');
  }, [currentUploadController]);

  /**
   * ファイルを管理リストから削除
   */
  const removeFile = useCallback((fileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  }, []);

  /**
   * エラー状態をクリア
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * 全ての状態をリセット
   */
  const reset = useCallback(() => {
    console.log('🧹 useFileUpload reset called - clearing uploaded files:', uploadedFiles.length);
    setUploadProgress(0);
    setIsUploading(false);
    setError(null);
    setUploadedFiles([]);
    setCurrentUploadController(null);
    console.log('✅ useFileUpload reset completed');
  }, [uploadedFiles.length]);

  return {
    uploadFile,
    uploadProgress,
    isUploading,
    error,
    uploadedFiles,
    cancelUpload,
    removeFile,
    clearError,
    reset
  };
};